import { Children, FC, ReactNode, useEffect, useRef, useState } from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";

import Slider from "@ant-design/react-slick/types";

import {
	StyledCarousel,
	StyledCarouselContainer,
	StyledCarouselItem,
} from "./Carousel.styles";
import { CarouselProps, CarouselSettings } from "./Carousel.types";

import CarouselArrow from "./CarouselArrow";
import CarouselScrollBar from "./CarouselScrollBar";
import PaginationDots from "../PaginationDots";
import Box from "../../layout/boxes/Box/Box";

const Carousel: FC<CarouselProps> = ({
	className,
	children,
	activeSlideIndex = 0,
	slidesToShow = 1,
	showArrows = true,
	showPagination = true,
	onPageChange = () => {},
	paginationVariant = "scrollbar",
}) => {
	const block = new BemIt("Carousel");

	const [currentPage, setCurrentPage] = useState<number>(activeSlideIndex);
	const sliderRef = useRef<Slider>(null);

	const slideCount = Children.count(children);
	const pageCount = Math.ceil(slideCount / slidesToShow);

	const handleSlideChange = (current: number, next: number) => {
		const nextPage = Math.ceil(next / slidesToShow);
		setCurrentPage(nextPage);
		onPageChange(nextPage);
	};

	useEffect(() => {
		const page = Math.floor(activeSlideIndex / slidesToShow);
		const nextPage = page < pageCount ? page : pageCount - 1;

		if (sliderRef.current) {
			sliderRef.current.slickGoTo(nextPage * slidesToShow);
		}
	}, [activeSlideIndex, pageCount, slidesToShow]);

	const settings: CarouselSettings = {
		fade: slidesToShow === 1,
		dots: showPagination && paginationVariant === "dots",
		slidesToShow,
		slidesToScroll: slidesToShow,
		infinite: false,
		arrows: showArrows,
		prevArrow: <CarouselArrow direction="prev" slidesToShow={slidesToShow} />,
		nextArrow: <CarouselArrow direction="next" slidesToShow={slidesToShow} />,
		beforeChange: handleSlideChange,
		appendDots: (dots: ReactNode[]) => {
			const numberOfDots = dots.length;

			const handlePageChange = (currentIndex: number) => {
				const sliderNode = sliderRef.current;

				if (sliderNode) sliderNode.slickGoTo(currentIndex * slidesToShow);
			};

			return (
				<Box
					className=""
					width="100%"
					display="flex"
					justifyContent="center"
					marginTop="lg"
				>
					<PaginationDots
						numberOfDots={numberOfDots}
						activeIndex={currentPage}
						onPageChange={handlePageChange}
					/>
				</Box>
			);
		},
	};

	return (
		<StyledCarouselContainer>
			<StyledCarousel
				className={addClass(className).after(block)}
				ref={sliderRef}
				{...settings}
			>
				{Children.map(children, (child, index) => (
					<StyledCarouselItem
						key={index}
						index={slidesToShow > 1 ? index : slideCount - index}
						className={
							block
								.el("item")
								.mod(
									slidesToShow === 1 && currentPage <= index ? "active" : null
								).out
						}
						slidesToShow={slidesToShow}
					>
						{child}
					</StyledCarouselItem>
				))}
			</StyledCarousel>
			{showPagination &&
				paginationVariant === "scrollbar" &&
				slidesToShow > 1 && (
					<CarouselScrollBar
						className={block.el("scrollbar").out}
						pageCount={pageCount}
						currentPage={currentPage}
						slidesToShow={slidesToShow}
						sliderNode={sliderRef}
					/>
				)}
		</StyledCarouselContainer>
	);
};

export default Carousel;
